import { useState } from 'react';

import { promptInstall, useInstallAvailable } from './install.js';
import {
  FINAL_NOTE,
  browserOf,
  dismissTips,
  isNewAccount,
  isStandalone,
  readTipsDismissed,
  tipsFor,
} from './tips.js';

import type { BrowserInfo, Tip } from './tips.js';
import type { Identity } from '../../core/auth/session.js';

/**
 * Карточка подсказок над списком (3.16.10).
 *
 * Подсказки идут по одной, а не списком: четыре абзаца разом — это та же
 * инструкция, от которой и уходили. Скрыть можно в любой момент, и скрытые не
 * возвращаются на этом устройстве.
 *
 * Состояние живёт в хуке, а не в карточке: подсветку того, о чём говорит
 * подсказка, ставит оболочка атрибутом `data-tip`, и ей нужно знать текущую
 * подсказку, не заглядывая в разметку карточки.
 */

export interface OnboardingTips {
  current: Tip | undefined;
  index: number;
  total: number;
  /** Браузер умеет показать системное окно установки (`install.ts`). */
  canInstall: boolean;
  next: () => void;
  dismiss: () => void;
  install: () => void;
}

export function useOnboardingTips(options: {
  identity: Identity | undefined;
  narrow: boolean;
  mouse: boolean;
  now?: Date;
  /** Подменяются в тестах; по умолчанию спрашиваются у браузера. */
  standalone?: boolean;
  browser?: BrowserInfo;
  canInstall?: boolean;
  promptInstall?: () => Promise<boolean>;
}): OnboardingTips {
  const [dismissed, setDismissed] = useState(readTipsDismissed);
  const [index, setIndex] = useState(0);
  const installAvailable = useInstallAvailable();

  const list = tipsFor({
    narrow: options.narrow,
    mouse: options.mouse,
    standalone: options.standalone ?? isStandalone(),
    browser: options.browser ?? browserOf(globalThis.navigator?.userAgent ?? ''),
  });
  const total = list.length;
  // Телефон повернули посреди подсказок, и их стало меньше, — остаёмся на
  // последней, а не выпадаем за край.
  const position = Math.min(index, total - 1);
  const eligible = !dismissed && isNewAccount(options.identity, options.now ?? new Date());

  function dismiss(): void {
    dismissTips();
    setDismissed(true);
  }

  return {
    current: eligible ? list[position] : undefined,
    index: position,
    total,
    canInstall: options.canInstall ?? installAvailable,
    next: () => {
      if (position + 1 >= total) dismiss();
      else setIndex(position + 1);
    },
    dismiss,
    install: () => {
      // Установили — подсказки своё дело сделали. Отказались — остаёмся на
      // месте: шаги через меню никуда не делись.
      void (options.promptInstall ?? promptInstall)().then((accepted) => {
        if (accepted) dismiss();
      });
    },
  };
}

export function TipCard({ tips }: { tips: OnboardingTips }): React.ReactElement | null {
  const { current, index, total } = tips;
  if (current === undefined) return null;

  const last = index + 1 >= total;
  // Системное окно лучше любых шагов: не надо искать пункт меню. Шаги
  // остаются запасным путём — в свёрнутом виде, окно браузер может и не дать.
  const offerInstall = current.id === 'home' && tips.canInstall;

  return (
    <section className="tips" aria-label="Подсказки">
      <div className="tips__head">
        <span>
          Подсказка {index + 1} из {total}
        </span>
        <button type="button" className="tips__skip" onClick={tips.dismiss}>
          Скрыть подсказки
        </button>
      </div>

      <h2 className="tips__title">{current.title}</h2>
      <p className="tips__body">{current.body}</p>

      {offerInstall && (
        <button type="button" className="tips__next tips__install" onClick={tips.install}>
          Установить
        </button>
      )}

      {current.steps !== undefined &&
        (offerInstall ? (
          <details className="tips__more">
            <summary>Или через меню браузера</summary>
            <Steps steps={current.steps} />
          </details>
        ) : (
          <Steps steps={current.steps} />
        ))}

      {last && <p className="tips__body">{FINAL_NOTE}</p>}

      <div className="tips__foot">
        <span className="tips__dots" aria-hidden="true">
          {Array.from({ length: total }, (_, dot) => (
            <span key={dot} className="tips__dot" data-current={dot === index} />
          ))}
        </span>
        <button type="button" className="tips__next" onClick={tips.next}>
          {last ? 'Понятно' : 'Дальше'}
        </button>
      </div>
    </section>
  );
}

function Steps({ steps }: { steps: readonly string[] }): React.ReactElement {
  return (
    <ol className="tips__steps">
      {steps.map((step) => (
        <li key={step}>{step}</li>
      ))}
    </ol>
  );
}
